import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ProductLightboxPinchImage } from './ProductLightboxPinchImage';
import { parseMotorHandbookCopy } from '@/lib/motorHandbookCopy';

export type MotorHandbookDetailPayload = {
  modelName: string;
  sectionTitle: string;
  imageUrl?: string;
  body: string;
};

type Props = {
  detail: MotorHandbookDetailPayload;
  onClose: () => void;
};

function SpecLine({ text }: { text: string }) {
  const m = text.match(/^([^:]{2,36}):\s*(.+)$/);
  if (m && m[2].trim()) {
    return (
      <p className="text-[11px] leading-snug" style={{ color: 'var(--text-2)' }}>
        <span className="font-bold" style={{ color: 'var(--text-1)' }}>
          {m[1].trim()}:
        </span>{' '}
        {m[2].trim()}
      </p>
    );
  }
  return (
    <p className="text-[11px] leading-snug pl-3 relative" style={{ color: 'var(--text-2)' }}>
      <span
        className="absolute left-0 top-[0.45em] w-1 h-1 rounded-full"
        style={{ background: 'var(--cyan)' }}
        aria-hidden
      />
      {text}
    </p>
  );
}

/**
 * Карточка мотора: фото + ТТХ (5 секций, подзаголовки внутри — как эталон MR-2234).
 */
export function MotorHandbookDetailSheet({ detail, onClose }: Props) {
  const sections = parseMotorHandbookCopy(detail.body);
  const imageUrl = String(detail.imageUrl || '').trim();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex flex-col"
      style={{
        background:
          'radial-gradient(120% 80% at 50% 0%, rgba(26, 110, 181, 0.18) 0%, transparent 55%), #0A192F',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`${detail.modelName} — техничка`}
      onClick={onClose}
    >
      <div className="relative z-10 flex flex-1 min-h-0 flex-col" onClick={(e) => e.stopPropagation()}>
        <div
          className="shrink-0 flex items-center justify-between gap-3 mx-3 mt-2 px-4 py-2.5 rounded-2xl glass"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.28)' }}
        >
          <div className="min-w-0">
            <p
              className="text-[10px] font-bold uppercase tracking-[0.2em] truncate"
              style={{ color: 'var(--cyan-bright)' }}
            >
              {detail.sectionTitle}
            </p>
            <p className="text-sm font-extrabold leading-tight truncate" style={{ color: 'var(--text-1)' }}>
              {detail.modelName}
            </p>
          </div>
          <button
            type="button"
            className="w-10 h-10 rounded-full flex items-center justify-center press shrink-0"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-subtle)' }}
            onClick={onClose}
            aria-label="Закрыть"
          >
            <X size={20} style={{ color: 'var(--text-1)' }} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pt-3 pb-4 space-y-3"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {imageUrl ? (
            <div
              className="w-full rounded-2xl overflow-hidden"
              style={{
                background: '#ffffff',
                border: '1px solid rgba(99,179,237,0.28)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
              }}
            >
              <div className="max-h-[38vh] flex items-center justify-center">
                <ProductLightboxPinchImage
                  src={imageUrl}
                  alt={detail.modelName}
                  slideKey={`${detail.modelName}-${imageUrl}`}
                  sizeMode="width"
                />
              </div>
            </div>
          ) : (
            <div
              className="rounded-2xl px-4 py-8 text-center glass-light"
              style={{ border: '1px dashed var(--border-subtle)' }}
            >
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>
                Фото мотора можно добавить в админке
              </p>
            </div>
          )}

          <div
            className="rounded-2xl p-4 glass-light"
            style={{ border: '1px solid var(--cyan-border)', boxShadow: '0 8px 28px rgba(0,0,0,0.2)' }}
          >
            {sections.length === 0 ? (
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                Текст технички пока не задан.
              </p>
            ) : (
              <div className="space-y-5">
                {sections.map((section) => (
                  <div key={section.title}>
                    <p
                      className="text-[11px] font-extrabold uppercase tracking-wide mb-2.5"
                      style={{ color: 'var(--text-1)' }}
                    >
                      {section.title}
                    </p>
                    <div className="space-y-2.5">
                      {section.items.map((item, ii) => (
                        <div key={`${section.title}-${ii}`}>
                          {item.label ? (
                            <p
                              className="text-[10px] font-bold uppercase tracking-wide mb-1"
                              style={{ color: 'var(--cyan-bright)' }}
                            >
                              {item.label}
                            </p>
                          ) : null}
                          <SpecLine text={item.text} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
